import React, { useState } from "react";

import { Box, Typography } from "@mui/material";
import { formatNumber } from "libphonenumber-js";
import { has } from "lodash";
import { makeStyles } from "tss-react/mui";

import { EditIconButton } from "@/components/IconButtons";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";

import EditPersonalInfoModal from "./components/EditPersonalInfoModal";

interface Props {
  clientId: string;
}

const useStyles = makeStyles()({
  section: {
    padding: 24,
    backgroundColor: colors.white,
    width: "100%",
  },
});

const personalInfoSectionFragment = graphql(`
  fragment PersonalInfoSection on Client {
    id
    email
    ghostEmail
    account {
      id
    }
    profile {
      id
      firstName
      middleName
      lastName
      fullName
      mobileNumber
      landlineNumber
      dateOfBirth
      country
      occupation
      gender
    }
  }
`);

const PersonalInfoSection: React.FC<Props> = ({ clientId }) => {
  const { classes } = useStyles();

  const [client] = useFragment(
    personalInfoSectionFragment,
    `Client:${clientId}`
  );
  const profile = client?.profile;
  const [modalVisibility, setModalVisibility] = useState<boolean>();
  const snackbar = useGlobalSnackbar();

  const handleModalClose = () => {
    setModalVisibility(false);
  };

  const handleModalOpen = () => {
    setModalVisibility(true);
  };

  const handleSnackBar = (isSuccessful: boolean, message?: string) => {
    if (isSuccessful) {
      snackbar.open("Successfully updated");
    } else {
      snackbar.open(
        message ??
          "There was an error in updating your clients personal information"
      );
    }
  };

  return (
    <>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          Personal Information
        </Typography>
        <Box marginY={3} />
        <EditIconButton onClick={handleModalOpen} />
      </Box>
      <section className={classes.section}>
        <Box
          width="100%"
          display="grid"
          alignItems="start"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
          }}
          columnGap={2}
          rowGap={2}
        >
          <Box>
            <Typography variant="body2" color="textSecondary">
              Name
            </Typography>
            <Typography variant="body2">{profile?.fullName}</Typography>
            <Box height={15} />
            <Typography variant="body2" color="textSecondary">
              Mobile number
            </Typography>
            <Typography variant="body2">
              {profile?.mobileNumber || "N/A"}
            </Typography>
          </Box>
          <Box minWidth={0}>
            <Typography variant="body2" color="textSecondary">
              Email
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
              {client?.email}
            </Typography>
            <Box height={15} />
            <Typography variant="body2" color="textSecondary">
              Landline number
            </Typography>
            <Typography variant="body2">
              {profile?.landlineNumber
                ? formatNumber(profile.landlineNumber, "NATIONAL")
                : "N/A"}
            </Typography>
          </Box>
          <div>
            <Typography variant="body2" color="textSecondary">
              Date of birth
            </Typography>
            <Typography variant="body2">
              {profile?.dateOfBirth != undefined
                ? new Date(profile?.dateOfBirth).toDateString()
                : "N/A"}
            </Typography>
          </div>
          {Features.clientListWorkplaceEnabled && (
            <div>
              <Typography variant="body2" color="textSecondary">
                Package start to end
              </Typography>
              <Typography variant="body2">
                10 march, 2020 - 20 march 2021
              </Typography>
            </div>
          )}
        </Box>
        <EditPersonalInfoModal
          visible={modalVisibility}
          onClose={handleModalClose}
          showSnackBar={handleSnackBar}
          profile={profile}
          email={client?.email ?? undefined}
          isRegistered={has(client?.account, "id")}
          loading={!client}
        />
      </section>
    </>
  );
};

export default PersonalInfoSection;
